import { useState, useMemo } from 'react'
import { Spinner } from '@heroui/react'
import { YearPeriodPicker } from '../reports/shared/YearPeriodPicker'
import { YearRangePicker } from '../reports/shared/YearRangePicker'
import { useYearPeriodStore } from '../../store/useYearPeriodStore'
import { useYearRangeStore } from '../../store/useYearRangeStore'
import { GroupPeriodGrid, periodTimeCols } from './GroupPeriodGrid'
import { GroupLoadGate } from './shared/GroupLoadGate'
import { HeatLegend } from './shared/HeatLegend'
import { StationLabelPicker } from './shared/StationLabelFields'
import { useStationLabelStore } from '../../store/useStationLabelStore'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import { TableOrientationToggle } from '../reports/shared/TableOrientationToggle'
import { useGroupPeriodData } from '../../hooks/useGroupPeriodData'
import { useGroupPeriodNormals } from '../../hooks/useGroupPeriodNormals'
import { unionNodeGhIds } from '../../utils/computeGroupTree'
import { GROUP_MLY_BY_SLUG, GROUP_YLY_BY_SLUG, GROUP_PERIOD_GATE_THRESHOLD } from '../../config/groupPeriodConfig'

// Skupinový produkt "Měsíční / Roční data – skupina" — řádky = stanice, sloupce = čas (měsíce+Rok /
// roky), 1 vybraná veličina (chip). Rozhodnutí list vs. kategorie stejně jako StationGroupMeasView.
// Viz doc/plans/station-group-monthly-yearly-phenomena.md sekce 4.
export function StationGroupPeriodView({ kind, productSlug, selectedNode, selectedPath, stationsByGhId, filteredTree }) {
  const def = kind === 'mly' ? GROUP_MLY_BY_SLUG.get(productSlug) : GROUP_YLY_BY_SLUG.get(productSlug)

  const { year, setYear } = useYearPeriodStore()
  const { yearFrom, yearTo, setYearFrom, setYearTo } = useYearRangeStore()
  const { labelFields, setLabelFields } = useStationLabelStore()
  const orientation = useTableOrientationStore((s) => s.getOrientation(productSlug))

  const isSingleLeaf = !!selectedNode && selectedNode.children.length === 0
  const nodes = selectedNode ? (isSingleLeaf ? [selectedNode] : selectedNode.children) : filteredTree

  const ghIds = useMemo(() => {
    const set = new Set()
    for (const n of nodes) for (const gh of unionNodeGhIds(n)) set.add(gh)
    return [...set]
  }, [nodes])

  const tsIds = useMemo(() => def?.cols.map(c => c.tsId) ?? [], [def])
  const period = { year, yearFrom, yearTo }

  // Gate: klíč = množina stanic + produkt. Změna kterékoli => remount => reset potvrzení.
  const gateKey = `${[...ghIds].sort().join(',')}::${productSlug}`
  const [confirmedKey, setConfirmedKey] = useState(null)
  const needsGate = !isSingleLeaf && ghIds.length > GROUP_PERIOD_GATE_THRESHOLD
  const confirmed = !needsGate || confirmedKey === gateKey

  const heading = selectedPath.length > 0 ? selectedPath[selectedPath.length - 1].bucketLabel : 'Všechny stanice'

  const { dataMap, isLoading: dLoading, isError } = useGroupPeriodData(kind, ghIds, tsIds, period, confirmed)
  const { normalsMap, isLoading: nLoading } = useGroupPeriodNormals(kind, ghIds, tsIds, confirmed && !!def?.normals)
  const isLoading = dLoading || nLoading

  const timeCols = useMemo(() => periodTimeCols(kind, period), [kind, year, yearFrom, yearTo]) // eslint-disable-line

  if (!def) return null

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-gray-700">
          {heading} — {def.label}
        </h2>
        {kind === 'mly'
          ? <YearPeriodPicker year={year} onYearChange={setYear} obsType="DLY" />
          : <YearRangePicker yearFrom={yearFrom} yearTo={yearTo} onYearFromChange={setYearFrom} onYearToChange={setYearTo} />}
        <div className="flex items-center gap-4 flex-wrap">
          <TableOrientationToggle slug={productSlug} />
          <StationLabelPicker labelFields={labelFields} onChange={setLabelFields} />
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {!confirmed && (
          <GroupLoadGate
            stationCount={ghIds.length}
            requestNote={`Načte se ${ghIds.length}× ${tsIds.length} časových řad${def.normals ? ' + normály' : ''}.`}
            onConfirm={() => setConfirmedKey(gateKey)}
          />
        )}
        {confirmed && (
          <div className="flex-1 min-h-0 p-4 flex gap-6">
            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Spinner size="sm" /> Načítání dat…
              </div>
            )}
            {isError && <p className="text-red-500 text-sm">Chyba při načítání dat</p>}
            {!isLoading && !isError && (
              <>
                <div className="flex-1 min-w-0">
                  <GroupPeriodGrid
                    kind={kind}
                    def={def}
                    nodes={nodes}
                    stationsByGhId={stationsByGhId}
                    timeCols={timeCols}
                    dataMap={dataMap}
                    normalsMap={normalsMap}
                    labelFields={labelFields}
                    orientation={orientation}
                  />
                </div>
                {def.normals && (
                  <HeatLegend variant="diverging" mode={def.normalMode ?? 'diff'} unit={def.unit} />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
